import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FooterDataDto,
  SortColumnDto,
  TableCacheKeyDto,
  TableColumnConfigDto,
  TableDataDto,
  TableDto,
} from '@asset-metrix/portal-gateway-client-js';
import {
  AG_GRID_GROUP_AUTO_COLUMN_ID,
  OneColumnGroup,
  Table as UiTable,
  TableSidebarWidget,
  TwoColumnGroup,
  Widget,
  WidgetWithPaginator,
} from '@asset-metrix/portal-ui';
import { useUncontrolled } from '@mantine/hooks';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  ColumnResizedEvent,
  GROUP_AUTO_COLUMN_ID,
  RowClickedEvent,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { useShallow } from 'zustand/react/shallow';

import {
  fetchDatatableData,
  FetchDatatableDataQueryKey,
  FetchDatatableDataQueryKeyParams,
} from '../../api/portalGateway/datatableData';
import { fetchPage, PageQueryKey } from '../../api/portalGateway/page';
import { useStore } from '../../store';
import { useFilterSelections } from '../../store/hooks/useFilterSelections';
import { TableGroupExpansionsValue } from '../../store/states/tableGroupExpansions';
import { extractErrorMessage } from '../../utils/errorMessage';
import { pixelSizeHelperPortalUi } from '../../utils/pixelSizeHelperPortalUi';
import { prependRoleId } from '../../utils/prependRoleId';
import { CustomTableFilters } from '../filter/customTableFilter/CustomTableFilters';
import { PageFilters } from '../filter/PageFilters';
import { TableFilters } from '../filter/TableFilters';
import { WidgetFilterContainer } from '../filter/WidgetFilterContainer';
import { WorkflowStateFilters } from '../filter/WorkflowStateFilters';

import { RelatedDocumentsLoader } from './sidebar/RelatedDocumentsLoader';
import {
  TableSidebarProps,
  useTableSidebarProperties,
} from './sidebar/useTableSidebarProperties';
import { configToColDef } from './configToColDef';
import { PAGINATION_SIZES } from './Table';
import { CustomColumnWidths } from './useCustomization';
import { useFooter } from './useFooter';
import { useGroupColumnHeaderName } from './useGroupColumnHeaderName';
import { usePageFilterColumnState } from './usePageFilterColumnState';
import {
  BaseUseTableWidgetActionProps,
  useTableWidgetActions,
} from './useTableWidgetActions';

const useTableDataQuery = (
  pageId: string,
  widgetId: string,
  cacheKey: TableCacheKeyDto,
  queryParams: FetchDatatableDataQueryKeyParams,
  enabled: boolean
) =>
  useQuery({
    queryKey: prependRoleId<FetchDatatableDataQueryKey>([
      'datatables',
      cacheKey,
      { pageId },
      { widgetId },
      queryParams,
    ]),
    queryFn: fetchDatatableData,
    placeholderData: keepPreviousData,
    enabled,
  });

export type TableContentProps = {
  activeCustomFilterIds: string[];
  collapsible: boolean;
  customizedColumns: TableColumnConfigDto[];
  defaultPageSize?: number;
  defaultSortItems?: SortColumnDto[];
  enableScrollElementToTop?: boolean;
  entity: TableDto;
  groupColumnWidth?: number;
  isColumnInitiallyHidden: boolean;
  isFullScreenMode?: boolean;
  onColumnWidthChanged?: (item: CustomColumnWidths) => void;
  onSortItemsChanged?: (sortItems: SortColumnDto[]) => void;
  pageFilterInstanceId: string;
  pageId: string;
  sortItems?: SortColumnDto[];
  widgetFilterInstanceId: string;
} & BaseUseTableWidgetActionProps;

export function TableContent({
  activeCustomFilterIds,
  collapsible,
  customizedColumns,
  defaultPageSize = 12,
  defaultSortItems,
  enableScrollElementToTop,
  entity,
  groupColumnWidth,
  isFullScreenMode = false,
  onColumnWidthChanged,
  onModalOpened,
  onSortItemsChanged,
  pageFilterInstanceId,
  pageId,
  sortItems,
  title,
  widgetFilterInstanceId,
  widgetId,
  ...useTableWidgetActionProps
}: TableContentProps) {
  const { t } = useTranslation();

  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(defaultPageSize);
  const [currentSortItems, setCurrentSortItems] = useUncontrolled({
    value: sortItems,
    defaultValue: defaultSortItems,
    onChange: onSortItemsChanged,
    finalValue: entity.config.sortColumns,
  });

  const [currentSelectedRows, setCurrentSelectedRows] = useState<
    TableDataDto['items']
  >([]);

  const { data: page } = useQuery({
    queryKey: prependRoleId<PageQueryKey>(['page', pageId ?? 'unknown']),
    queryFn: fetchPage,

    enabled: isFullScreenMode,
  });

  const { meta: pageFilterMeta, visualisedFilter: pageFilter } = useStore(
    (state) => state.filterData?.[pageFilterInstanceId]
  ) || {
    meta: undefined,
    visualisedFilter: undefined,
  };

  const { meta: tableFilterMeta, visualisedFilter: tableFilter } = useStore(
    (state) => state.filterData?.[widgetFilterInstanceId]
  ) || {
    meta: undefined,
    visualisedFilter: undefined,
  };

  const {
    currentSigningRequests,
    isPageFilterLoading,
    isSigningInProgress,
    storeSetTableGroupExpansion,
    storeWorkflowStateSelection,
    tableGroupExpansions,
  } = useStore(
    useShallow((state) => ({
      currentSigningRequests: state.currentSigningRequests,
      isPageFilterLoading: state.isPageFilterLoading,
      isSigningInProgress: state.isSigningInProgress(),
      storeSetTableGroupExpansion: state.setTableGroupExpansion,
      storeWorkflowStateSelection:
        state.workflowStateSelections[widgetFilterInstanceId],
      tableGroupExpansions: state.tableGroupExpansions[widgetFilterInstanceId],
    }))
  );

  const workflowStateSelection = useMemo(
    () => storeWorkflowStateSelection?.map(({ states }) => states),
    [storeWorkflowStateSelection]
  );

  const pageFilterSelections = useFilterSelections(pageFilterInstanceId);
  const tableFilterSelections = useFilterSelections(widgetFilterInstanceId);

  const actions = useTableWidgetActions({
    activeCustomFilterIds,
    config: entity.config,
    currentSelectedRows,
    pageFilter,
    pageFilterSelections,
    tableFilterSelections,
    workflowStateSelection,
    onModalOpened,
    widgetId,
    title,
    ...useTableWidgetActionProps,
  });

  const setTableGroupExpansion = useCallback(
    (groupId: string, value: TableGroupExpansionsValue) => {
      storeSetTableGroupExpansion(widgetFilterInstanceId, groupId, value);
    },
    [storeSetTableGroupExpansion, widgetFilterInstanceId]
  );

  // page filter columns
  const pageFilterColumn = useMemo(
    () =>
      customizedColumns.find(
        (c) => c.type === 'pageFilter' && c.filterKey !== null
      ),
    [customizedColumns]
  );

  const {
    isChecked: isPageFilterColumnChecked,
    onChange: setFilterFromPageFilterColumn,
  } = usePageFilterColumnState({
    pageFilterColumn,
    pageFilterInstanceId,
    pageFilterSelections,
  });

  // Reset current pagination page when page or table filters are changed
  useEffect(() => {
    setCurrentPage(0);
  }, [pageFilter, tableFilter, workflowStateSelection]);

  // @todo: persist active query key in UI Store to be able to restore last "state" on mount
  const queryKey: FetchDatatableDataQueryKeyParams = {
    page: currentPage,
    pageSize: currentPageSize,
    pageFilter: pageFilterSelections,
    tableFilter: tableFilterSelections,
    workflowStateSelection,
    sortItems: currentSortItems,
  };

  const shouldRequestTableData =
    entity.config.columns.length > 0 &&
    pageFilterMeta?.loaded === true &&
    tableFilterMeta?.loaded === true;

  const { data, isFetching, error } = useTableDataQuery(
    pageId,
    widgetId,
    entity.config.cacheKey,
    queryKey,
    shouldRequestTableData
  );

  const rowData = useMemo(() => {
    return data?.data.items.map((item, idx) => ({
      ...item,
      originalRowIndex: idx,
    }));
  }, [data?.data.items]);

  const dataConfigs = data?.data.configs;
  const totalRows = data?.data.meta.totalCount ?? 0;

  const firstVisibleCustomizedColumn = customizedColumns.find(
    (col) => !col.hidden
  );

  const footerTitleColumnKey = entity.config.isGroupedTable
    ? GROUP_AUTO_COLUMN_ID
    : firstVisibleCustomizedColumn?.key;

  const { data: footer, isLoading: isFooterLoading } = useFooter({
    widgetId,
    tableConfig: entity.config,
    pageFilterInstanceId,
    widgetFilterInstanceId,
    titleColumnKey: footerTitleColumnKey,
    shouldRequestFooterData: shouldRequestTableData,
  });

  const { props: sidebarProps, toggle: toggleSidebar } =
    useTableSidebarProperties(entity.config);

  // Always close sidebar when table data changes
  useEffect(() => {
    if (sidebarProps?.onClose && data?.data.items) {
      sidebarProps.onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.data.items]);

  const columnDefs = useMemo(() => {
    // Find the deepest group level
    const deepestGroupLevel = Math.max(
      ...customizedColumns
        .map((col) => col.grouping)
        .filter((v): v is number => v != null)
    );

    // check if any top level group is expanded
    const isSomeTopLevelGroupExpanded =
      tableGroupExpansions != null
        ? Object.values(tableGroupExpansions).some(
            ({ groupLevel, expanded }) => groupLevel === 0 && expanded
          )
        : entity.config.groupDefaultExpanded != null;

    return customizedColumns.map((col) =>
      configToColDef(col, widgetId, isFooterLoading, customizedColumns, {
        currentSigningRequests,
        dataConfigs,
        deepestGroupLevel,
        footerData: footer,
        footerTitleColumnKey: footerTitleColumnKey,
        isFullScreenMode,
        isGroupedTable: entity.config.isGroupedTable,
        isPageFilterColumnChecked,
        isSigningInProgress,
        isSomeTopLevelGroupExpanded,
        pageFilter,
        rowIdToHighlight: sidebarProps?.data?.rowId,
        setFilterFromPageFilterColumn,
        showFullTextSearchResults: entity.config.showFullTextSearchResults,
        workflowActionHandler: onModalOpened,
      })
    );
  }, [
    currentSigningRequests,
    customizedColumns,
    dataConfigs,
    entity.config.groupDefaultExpanded,
    entity.config.isGroupedTable,
    entity.config.showFullTextSearchResults,
    footer,
    footerTitleColumnKey,
    isFooterLoading,
    isFullScreenMode,
    isPageFilterColumnChecked,
    isSigningInProgress,
    onModalOpened,
    pageFilter,
    setFilterFromPageFilterColumn,
    sidebarProps?.data?.rowId,
    tableGroupExpansions,
    widgetId,
  ]);

  const onSelectionChanged = useCallback(
    (event: SelectionChangedEvent<TableDataDto['items'][number]>) => {
      setCurrentSelectedRows(event.api.getSelectedRows());
    },
    []
  );

  const onRowClickedCb = useCallback(
    (event: RowClickedEvent<TableDataDto['items'][number]>) => {
      if (event.node.id != null) {
        toggleSidebar?.({
          rowId: event.node.id,
          data: event.data ?? {},
        });
      }
    },
    [toggleSidebar]
  );

  const onColumnResized = useCallback(
    (event: ColumnResizedEvent<TableDataDto['items'][number]>) => {
      if (
        onColumnWidthChanged &&
        event.source === 'uiColumnResized' &&
        // last resize event in a row
        event.finished
      ) {
        const { column } = event;

        // Auto group column has first grouping level set as "field" property, therefore we need the colId to uniquely identify the column
        const columnId =
          column?.getColId() === AG_GRID_GROUP_AUTO_COLUMN_ID
            ? AG_GRID_GROUP_AUTO_COLUMN_ID
            : (column?.getColDef().field ?? column?.getColId());

        const width = column?.getActualWidth();
        if (columnId && width) {
          onColumnWidthChanged({ [columnId]: { width } });
        }
      }
    },
    [onColumnWidthChanged]
  );

  const onSortingChanged = (sortItem: SortColumnDto) => {
    setCurrentSortItems([sortItem, ...currentSortItems.slice(1)]);
  };

  const displayedError: string | undefined = useMemo(() => {
    if (error != null) {
      return extractErrorMessage(error);
    }
  }, [error]);

  const hidePagination = useMemo(() => {
    return (
      entity.config.hidePagination === true ||
      (entity.config.hidePagination === 'opportunistic' &&
        totalRows <= Math.min(...PAGINATION_SIZES))
    );
  }, [entity.config.hidePagination, totalRows]);

  const pinnedBottomRowData: FooterDataDto['columns'][] = useMemo(() => {
    // footer data row
    const pinnedRows = footer?.columns != null ? [footer.columns] : [];
    // more entries indicator row when pagination is hidden
    if (
      hidePagination &&
      footerTitleColumnKey &&
      totalRows - currentPageSize > 0
    ) {
      pinnedRows.push({
        [footerTitleColumnKey]: t('app.table.showMoreEntry', '', {
          count: totalRows - currentPageSize,
        }),
      });
    }
    return pinnedRows;
  }, [
    footer?.columns,
    hidePagination,
    footerTitleColumnKey,
    totalRows,
    currentPageSize,
    t,
  ]);

  const showTableLoadingOverlay: boolean = useMemo(() => {
    return (
      displayedError == null &&
      (isFetching ||
        isPageFilterLoading ||
        (tableFilterMeta?.loaded !== true && tableFilterMeta?.error == null))
    );
  }, [
    displayedError,
    isFetching,
    isPageFilterLoading,
    tableFilterMeta?.error,
    tableFilterMeta?.loaded,
  ]);

  const groupColumnHeaderName = useGroupColumnHeaderName(customizedColumns);

  const tableContent = (
    <>
      <WidgetFilterContainer
        withBottomBorder={Boolean(
          entity.config.workflowStateSelectionConfig?.options.length
        )}
      >
        {isFullScreenMode && page != null ? (
          <PageFilters
            filterKey={pageFilterInstanceId}
            pageId={pageId}
            config={page.data.pageFilter}
            embedded={true}
          />
        ) : null}

        {entity.config.filters != null && (
          <TableFilters
            config={entity.config}
            pageFilterInstanceId={pageFilterInstanceId}
            widgetId={widgetId}
            widgetFilterInstanceId={widgetFilterInstanceId}
          />
        )}
        {activeCustomFilterIds.length > 0 && (
          <CustomTableFilters
            activeCustomFilterIds={activeCustomFilterIds}
            columnConfigs={entity.config.columns}
            pageFilterInstanceId={pageFilterInstanceId}
            widgetId={widgetId}
            widgetFilterInstanceId={widgetFilterInstanceId}
          />
        )}
      </WidgetFilterContainer>
      {
        /* add workflow state filters if present */
        entity.config.workflowStateSelectionConfig?.options.length && (
          <WorkflowStateFilters
            config={entity.config}
            widgetFilterInstanceId={widgetFilterInstanceId}
          />
        )
      }

      {columnDefs && (
        <UiTable
          columnDefs={columnDefs}
          enableRowSelection={entity.config.enableRowSelection}
          error={displayedError}
          // @tempfix: Find a better estimate for grouped table height
          expectedRowCount={entity.config.isGroupedTable ? 12 : currentPageSize}
          gridOptions={{
            headerHeight: pixelSizeHelperPortalUi(56),
            rowHeight: pixelSizeHelperPortalUi(48),
          }}
          groupColumnField={
            columnDefs.find((col) => col.rowGroupIndex === 0)?.field
          }
          groupColumnHeaderName={groupColumnHeaderName}
          groupColumnWidth={groupColumnWidth}
          isGroupOpenByDefault={(params) => {
            if (isFullScreenMode) {
              // expand all groups by default in fullscreen mode
              return true;
            }
            const state =
              params.rowNode.id != null
                ? tableGroupExpansions?.[params.rowNode.id]?.expanded
                : undefined;
            const groupDefaultExpanded = entity.config.groupDefaultExpanded;
            return (
              state ??
              (groupDefaultExpanded != null
                ? params.level < groupDefaultExpanded
                : false)
            );
          }}
          loading={showTableLoadingOverlay}
          onColumnResized={onColumnResized}
          onRowGroupOpened={(event) => {
            const { id, rowGroupIndex } = event.node;
            if (id != null && rowGroupIndex != null) {
              setTableGroupExpansion(id, {
                expanded: event.expanded,
                groupLevel: rowGroupIndex,
              });
            }
          }}
          onSelectionChanged={onSelectionChanged}
          onSortingChanged={onSortingChanged}
          onRowClickedCb={toggleSidebar ? onRowClickedCb : undefined}
          pinnedBottomRowData={pinnedBottomRowData}
          rowData={rowData}
          // let user only sort by one (primary) column
          // technically data could be sorted by up to three different columns
          sortItems={currentSortItems.slice(0, 1)}
        />
      )}
    </>
  );

  const tableWidget = hidePagination ? (
    <Widget
      actions={actions}
      collapsible={collapsible}
      enableScrollElementToTop={enableScrollElementToTop}
      title={title}
    >
      {tableContent}
    </Widget>
  ) : (
    <WidgetWithPaginator
      actions={actions}
      activePage={currentPage}
      activePaginationSize={currentPageSize}
      availablePaginationSizes={
        entity.config.isGroupedTable
          ? [...PAGINATION_SIZES, 250]
          : PAGINATION_SIZES
      }
      collapsible={collapsible}
      enableScrollElementToTop={enableScrollElementToTop}
      onPageChange={setCurrentPage}
      onPaginationSizeChange={setCurrentPageSize}
      title={title}
      totalEntries={totalRows}
      selectedRowsCount={currentSelectedRows.length}
      isDocViewWidget={entity.config.cacheKey.dataSourceType === 'docview'}
    >
      {tableContent}
    </WidgetWithPaginator>
  );

  return sidebarProps ? (
    <WithSidebar {...sidebarProps} widgetId={widgetId}>
      {tableWidget}
    </WithSidebar>
  ) : (
    tableWidget
  );
}

const WithSidebar = ({
  children,
  onClose,
  ...props
}: TableSidebarProps & { children: React.ReactNode; widgetId: string }) => {
  const { opened } = props;

  return (
    // height: 100% is required to fit in fullscreen modal
    <TwoColumnGroup style={{ height: '100%' }}>
      <OneColumnGroup>{children}</OneColumnGroup>
      {opened && (
        <OneColumnGroup slim>
          <TableSidebarWidget
            content={<RelatedDocumentsLoader {...props} />}
            onClose={onClose}
          />
        </OneColumnGroup>
      )}
    </TwoColumnGroup>
  );
};
